/* Bonorum v11 – Zahlen hochzählen, Produkt-Navigation, Scroll-Bildsequenz.
   Respektiert „Bewegung reduzieren“ (Systemeinstellung) und den Pause-Button der Seite. */
(() => {
  if (window.bonorumSectionsLoaded) return;
  window.bonorumSectionsLoaded = true;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const motionOn = () => !reduce.matches && !document.body.classList.contains('bn-motion-paused');
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

  /* ---------- 1. Zahlen, die hochzählen ---------- */
  const fmt = new Intl.NumberFormat(document.documentElement.lang || 'de-DE');
  const countSeen = new WeakSet();
  let countObserver;
  const runCount = (el) => {
    const target = Number(el.dataset.bnCount);
    if (!Number.isFinite(target)) return;
    if (!motionOn() || target === 0) { el.textContent = fmt.format(target); return; }
    const duration = 1400, start = performance.now();
    const step = (now) => {
      const t = clamp((now - start) / duration);
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t); // easeOutExpo
      el.textContent = fmt.format(Math.round(target * eased));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const setupCounts = () => {
    const els = [...document.querySelectorAll('[data-bn-count]')].filter((el) => !countSeen.has(el));
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) return; // Endwert steht bereits im HTML
    countObserver = countObserver || new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        countObserver.unobserve(target);
        runCount(target);
      });
    }, { threshold: 0.6 });
    els.forEach((el) => {
      countSeen.add(el);
      // Nur auf 0 setzen, wenn die Zahl noch nicht im Bild ist (sonst springt sie sichtbar)
      const box = el.getBoundingClientRect();
      if (motionOn() && box.top > innerHeight) el.textContent = '0';
      countObserver.observe(el);
    });
  };

  /* ---------- 2. Produkt-Navigation: aktiven Abschnitt markieren ---------- */
  const setupLocalNav = () => {
    document.querySelectorAll('[data-bn-localnav]:not([data-ready])').forEach((nav) => {
      nav.dataset.ready = 'true';
      const links = [...nav.querySelectorAll('[data-bn-localnav-link]')];
      const targets = links.map((a) => document.getElementById(decodeURIComponent(a.hash.slice(1)))).filter(Boolean);
      if (!targets.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          links.forEach((a) => {
            const active = a.hash.slice(1) === e.target.id;
            a.classList.toggle('is-active', active);
            if (active) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-40% 0px -55% 0px' });
      targets.forEach((t) => io.observe(t));
      const sync = () => nav.classList.toggle('is-stuck', nav.getBoundingClientRect().top <= (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--theme-header-height')) || 0) + 1);
      addEventListener('scroll', sync, { passive: true });
      sync();
    });
  };

  /* ---------- 3. Scroll-Bildsequenz ---------- */
  class Sequence {
    constructor(root) {
      this.root = root;
      this.urls = (root.dataset.frames || '').split('|').filter(Boolean);
      this.track = root.querySelector('.bn-seq__track');
      this.canvas = root.querySelector('[data-bn-seq-canvas]');
      this.ctx = this.canvas.getContext('2d');
      this.captions = [...root.querySelectorAll('.bn-seq__caption')];
      this.bar = root.querySelector('.bn-seq__progress span');
      this.images = new Array(this.urls.length);
      this.loaded = new Array(this.urls.length).fill(false);
      this.current = -1;
      this.frame = 0;
      if (!this.urls.length) { this.setStatic(); return; }
      this.watch();
    }

    setStatic() {
      this.root.classList.add('bn-seq--static');
      this.root.classList.remove('bn-seq--ready');
    }

    watch() {
      const start = () => { if (!this.started) { this.started = true; this.load(); } };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
        }, { rootMargin: '150% 0px' });
        io.observe(this.root);
      } else start();
    }

    load() {
      // Erstes Bild prüft, ob die Bildfolge existiert. Fehlt es, bleibt das Ersatzbild stehen.
      const first = new Image();
      first.decoding = 'async';
      first.onload = () => {
        this.images[0] = first; this.loaded[0] = true;
        this.root.classList.add('bn-seq--ready');
        this.bind();
        this.queue();
      };
      first.onerror = () => this.setStatic();
      first.src = this.urls[0];
    }

    queue() {
      // Erst jedes 8. Bild (schnelle Grobfassung), dann die Lücken auffüllen. Max. 6 gleichzeitig.
      const order = [];
      for (let i = 8; i < this.urls.length; i += 8) order.push(i);
      for (let i = 1; i < this.urls.length; i++) if (i % 8) order.push(i);
      order.push(this.urls.length - 1);
      let next = 0, running = 0;
      const pump = () => {
        while (running < 6 && next < order.length) {
          const i = order[next++];
          if (this.images[i]) continue;
          const img = new Image();
          img.decoding = 'async';
          this.images[i] = img;
          running++;
          img.onload = () => { this.loaded[i] = true; running--; if (Math.abs(i - this.target()) < 4) this.draw(true); pump(); };
          img.onerror = () => { running--; pump(); };
          img.src = this.urls[i];
        }
      };
      pump();
    }

    bind() {
      this.resize();
      if ('ResizeObserver' in window) new ResizeObserver(() => this.resize()).observe(this.canvas.parentElement);
      addEventListener('scroll', () => this.schedule(), { passive: true });
      addEventListener('resize', () => this.schedule(), { passive: true });
      new MutationObserver(() => this.mode()).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      reduce.addEventListener('change', () => this.mode());
      this.mode();
    }

    mode() {
      const still = !motionOn();
      this.root.classList.toggle('bn-seq--still', still);
      this.current = -1;
      this.schedule();
    }

    resize() {
      const box = this.canvas.parentElement.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.canvas.width = Math.round(box.width * dpr);
      this.canvas.height = Math.round(box.height * dpr);
      this.current = -1;
      this.schedule();
    }

    progress() {
      if (!motionOn()) return 1; // Standbild: Endzustand (z. B. Explosionsansicht)
      const box = this.track.getBoundingClientRect();
      const travel = Math.max(1, box.height - innerHeight);
      return clamp(-box.top / travel);
    }

    target() { return Math.round(this.progress() * (this.urls.length - 1)); }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.draw(); });
    }

    nearest(i) {
      for (let d = 0; d < this.urls.length; d++) {
        if (this.loaded[i - d]) return i - d;
        if (this.loaded[i + d]) return i + d;
      }
      return 0;
    }

    draw(force) {
      const p = this.progress();
      const index = this.nearest(this.target());
      if (this.bar) this.bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      const pct = p * 100;
      this.captions.forEach((c) => {
        const from = Number(c.dataset.from), to = Number(c.dataset.to);
        const fade = 4;
        const o = motionOn() ? clamp(Math.min((pct - from) / fade, (to - pct) / fade)) : 1;
        c.style.opacity = o.toFixed(3);
        c.style.transform = motionOn() ? 'translateY(' + ((1 - o) * 16).toFixed(1) + 'px)' : '';
        c.classList.toggle('is-visible', o > 0.5);
      });
      if (index === this.current && !force) return;
      const img = this.images[index];
      if (!img || !this.loaded[index]) return;
      this.current = index;
      const cw = this.canvas.width, ch = this.canvas.height;
      const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
      this.ctx.clearRect(0, 0, cw, ch);
      this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
    }
  }
  const setupSequences = () => {
    document.querySelectorAll('[data-bn-seq]:not([data-ready])').forEach((el) => {
      el.dataset.ready = 'true';
      el.bnSequence = new Sequence(el);
    });
  };

  /* ---------- 4. Produkt-Tabs ---------- */
  const setupTabs = () => {
    document.querySelectorAll('[data-bn-tabs]:not([data-ready])').forEach((root) => {
      root.dataset.ready = 'true';
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (!panel) return;
          if (on) {
            panel.hidden = false;
            if (motionOn()) { panel.classList.remove('is-entering'); void panel.offsetWidth; panel.classList.add('is-entering'); }
          } else panel.hidden = true;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (e.key === 'Home') { e.preventDefault(); select(tabs[0], true); }
          else if (e.key === 'End') { e.preventDefault(); select(tabs[tabs.length - 1], true); }
          else if (dir) { e.preventDefault(); select(tabs[(i + dir + tabs.length) % tabs.length], true); }
        });
      });
    });
  };

  /* ---------- 5. Seitlich scrollbare Reihen: Pfeile am Desktop ---------- */
  const setupRails = () => {
    document.querySelectorAll('[data-bn-rail]:not([data-ready])').forEach((rail) => {
      rail.dataset.ready = 'true';
      const host = rail.closest('section');
      const nav = host && host.querySelector('[data-bn-rail-nav]');
      if (!nav) return;
      const [prev, next] = nav.querySelectorAll('button');
      const sync = () => {
        const overflow = rail.scrollWidth > rail.clientWidth + 4;
        nav.hidden = !overflow;
        prev.disabled = rail.scrollLeft < 4;
        next.disabled = rail.scrollLeft + rail.clientWidth > rail.scrollWidth - 4;
      };
      nav.querySelectorAll('button').forEach((b) => b.addEventListener('click', () => {
        rail.scrollBy({ left: Number(b.dataset.dir) * rail.clientWidth * 0.8, behavior: motionOn() ? 'smooth' : 'auto' });
      }));
      rail.addEventListener('scroll', sync, { passive: true });
      addEventListener('resize', sync, { passive: true });
      sync();
    });
  };

  /* ---------- 6. Aktions-Banner nach Enddatum ausblenden ---------- */
  const setupPromos = () => {
    document.querySelectorAll('[data-bn-promo-end]').forEach((el) => {
      const end = new Date(el.dataset.bnPromoEnd + 'T23:59:59');
      if (!isNaN(end) && Date.now() > end.getTime()) {
        const wrap = el.closest('.shopify-section') || el;
        wrap.hidden = true;
      }
    });
  };

  /* ---------- 7. Stores: live „Geöffnet / Geschlossen“ (deutsche Zeit) ---------- */
  const DAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const berlinNow = () => {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
    const get = (t) => (parts.find((p) => p.type === t) || {}).value;
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    return { day, minutes: Number(get('hour')) * 60 + Number(get('minute')) };
  };
  const toMin = (s) => { const [h, m] = String(s || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const fmtTime = (s) => { const [h, m] = String(s || '').split(':'); return Number(m) ? `${Number(h)}:${m}` : `${Number(h)}`; };
  const updateStores = () => {
    document.querySelectorAll('[data-bn-stores]').forEach((root) => {
      const open = toMin(root.dataset.open), close = toMin(root.dataset.close);
      const days = (root.dataset.days || '').split(',').filter(Boolean).map(Number);
      if (!days.length || close <= open) return;
      const { day, minutes } = berlinNow();
      const isOpen = days.includes(day) && minutes >= open && minutes < close;
      let next = '';
      if (!isOpen) {
        if (days.includes(day) && minutes < open) next = `öffnet heute um ${fmtTime(root.dataset.open)} Uhr`;
        else {
          for (let i = 1; i <= 7; i++) {
            const d = (day + i) % 7;
            if (days.includes(d)) { next = `öffnet ${i === 1 ? 'morgen' : DAY_NAMES[d]} um ${fmtTime(root.dataset.open)} Uhr`; break; }
          }
        }
      }
      root.querySelectorAll('[data-bn-store-status]').forEach((el) => {
        const card = el.closest('.bn-store');
        const forcedClosed = el.dataset.closed === 'true';
        const openNow = isOpen && !forcedClosed;
        el.querySelector('[data-bn-store-text]').textContent = openNow
          ? `Geöffnet · bis ${fmtTime(root.dataset.close)} Uhr`
          : (forcedClosed ? 'Vorübergehend geschlossen' : `Geschlossen · ${next}`);
        el.hidden = false;
        if (card) { card.classList.toggle('is-open', openNow); card.classList.toggle('is-closed', !openNow); }
      });
    });
  };
  const setupStores = () => {
    if (!document.querySelector('[data-bn-stores]')) return;
    updateStores();
    if (!window.bonorumStoreTimer) window.bonorumStoreTimer = setInterval(updateStores, 60000);
  };

  // v16: Footer – am Handy aufklappbare Spalten, am Computer immer offen
  const setupFooter = () => {
    const mq = window.matchMedia('(max-width: 767px)');
    document.querySelectorAll('[data-bn-footer]').forEach((footer) => {
      if (footer.dataset.bnReady) return;
      footer.dataset.bnReady = '1';
      const cols = [...footer.querySelectorAll('.bn-footer__col')];
      const apply = () => cols.forEach((col) => {
        if (!mq.matches || col.classList.contains('bn-footer__col--newsletter')) col.open = true;
        else col.open = false;
      });
      apply();
      mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
      cols.forEach((col) => col.querySelector('summary').addEventListener('click', (e) => {
        if (!mq.matches || col.classList.contains('bn-footer__col--newsletter')) e.preventDefault();
      }));
    });
  };

  const setup = () => { setupCounts(); setupLocalNav(); setupSequences(); setupTabs(); setupRails(); setupPromos(); setupStores(); setupFooter(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
  document.addEventListener('shopify:section:load', setup);
})();
